import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import Typography from '@mui/material/Typography';

import {
    readStoredTileStyleKey,
    storeTileStyleKey,
    TILE_STYLES,
} from 'src/components/map-tile-switcher/tile-styles';

import styles from 'src/components/map-tile-switcher/MapTileSwitcher.module.css';

type Props = {
    /** The Leaflet map to drive. Null until the owner has created it. */
    map: L.Map | null;
    /** Fired when the active layer fails to load tiles (rate limits, offline). */
    onTileError?: () => void;
    /** Positions the control within the owner's map chrome. */
    className?: string;
};

/**
 * Owns the base tile layer of a map and the little control that switches it between
 * street map and satellite imagery. The choice is remembered across surfaces, so
 * flipping the properties map to satellite flips the similar-properties map too.
 */
export default function MapTileSwitcher({ map, onTileError, className }: Props) {
    const [styleKey, setStyleKey] = useState(readStoredTileStyleKey);

    useEffect(() => {
        if (!map) {
            return;
        }
        const style = TILE_STYLES.find((candidate) => candidate.key === styleKey) ?? TILE_STYLES[0];
        const layer = L.tileLayer(style.url, {
            attribution: style.attribution,
            maxZoom: style.maxZoom,
        });
        if (onTileError) {
            layer.on('tileerror', onTileError);
        }
        layer.addTo(map);

        return () => {
            layer.remove();
        };
    }, [map, styleKey, onTileError]);

    const selectStyle = (key: string) => {
        setStyleKey(key);
        storeTileStyleKey(key);
    };

    return (
        <div className={className ? `${styles.control} ${className}` : styles.control}>
            {TILE_STYLES.map((style) => (
                <button
                    key={style.key}
                    type="button"
                    className={style.key === styleKey ?
                        `${styles.option} ${styles.optionActive}` :
                        styles.option}
                    onClick={() => selectStyle(style.key)}
                    data-testid={`map-tile-style-${style.key}`}
                >
                    <Typography variant="caption" component="span">
                        {style.label}
                    </Typography>
                </button>
            ))}
        </div>
    );
}
